import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlus, ClipboardCheck, Search, Trash2 } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ModalePlanificationEcf } from '../components/ecf/ModalePlanificationEcf';
import { useValidationDirecte } from '../components/modifications-sensibles/ElementsDemande';
import { ModaleSaisieEcf } from '../components/ecf/ModaleSaisieEcf';
import { jourLisible } from '../components/ecf/format';
import { usePerimetre } from '../site/usePerimetre';
import { useAuth } from '../auth/useAuth';
import { ErreurApi } from '../lib/api';
import { useFinFormation, type LigneFinFormation } from '../lib/useEspaceFormateur';
import { useEcf, useRetirerEcf, type LigneEcf } from '../lib/useEcf';
import { useNotifications } from '../lib/useNotifications';

/**
 * Fin de formation & ECF (CDC §7.5), espace formateur et pôle pédagogique.
 *
 * Les alertes de fin se lisent sur la même règle que le moteur d'alertes
 * (`domain/fin-formation.ts`) : un seul palier à la fois, une date de fin qui
 * dit d'où elle vient. Les ECF se planifient par promotion et se saisissent en
 * grille ; ce qui empêche un geste est rendu par l'API, ligne par ligne.
 */

type FiltreFin = 'ALERTE' | 'TOUS';
type FiltreEcf = 'A_SAISIR' | 'PLANIFIEE' | 'SAISIE' | 'TOUTES';

const FILTRES_ECF: { valeur: FiltreEcf; libelle: string }[] = [
  { valeur: 'A_SAISIR', libelle: 'À saisir' },
  { valeur: 'PLANIFIEE', libelle: 'Planifiées' },
  { valeur: 'SAISIE', libelle: 'Saisies' },
  { valeur: 'TOUTES', libelle: 'Toutes' },
];

/** Ce qui demande un geste : jalon atteint, fin passée sans clôture, date manquante. */
function enAlerte(ligne: LigneFinFormation): boolean {
  if (ligne.dateFin === null || ligne.palier !== null) return true;
  return ligne.joursAvantFin !== null && ligne.joursAvantFin < 0 && ligne.certificat !== 'DELIVRE';
}

function Puces<T extends string>({
  options,
  valeur,
  onChange,
  libelle,
}: {
  options: { valeur: T; libelle: string; nombre?: number }[];
  valeur: T;
  onChange: (valeur: T) => void;
  libelle: string;
}): JSX.Element {
  return (
    <div role="group" aria-label={libelle} className="flex flex-wrap gap-1.5">
      {options.map((option) => (
        <button
          key={option.valeur}
          type="button"
          aria-pressed={valeur === option.valeur}
          onClick={() => onChange(option.valeur)}
          className={`min-h-[32px] rounded-lg px-3 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
            valeur === option.valeur
              ? 'bg-codialis-navy text-white'
              : 'border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
          }`}
        >
          {option.libelle}
          {option.nombre !== undefined ? ` · ${option.nombre}` : ''}
        </button>
      ))}
    </div>
  );
}

function detailFin(ligne: LigneFinFormation): string {
  const morceaux: string[] = [];
  morceaux.push(
    ligne.dateFin
      ? `Fin le ${jourLisible(ligne.dateFin)}${ligne.provenanceFin ? ` (${ligne.provenanceFin})` : ''}`
      : 'Aucune date de fin : ni au dossier, ni au contrat, ni à la session',
  );
  if (ligne.dateExamen) morceaux.push(`examen le ${jourLisible(ligne.dateExamen)}`);
  if (ligne.mode === 'OF' && !ligne.dateExamen) morceaux.push('certificat de réalisation');
  return morceaux.join(' · ');
}

function NomApprenant({ id, nom }: { id: string; nom: string }): JSX.Element {
  const { peutAcceder } = useAuth();
  if (peutAcceder('adm-dossier') || peutAcceder('learners')) {
    return (
      <Link
        to={`/adm-dossier/${id}`}
        className="font-semibold text-codialis-navy hover:underline [@media(pointer:coarse)]:inline-flex [@media(pointer:coarse)]:min-h-[40px] [@media(pointer:coarse)]:items-center"
      >
        {nom}
      </Link>
    );
  }
  return <span className="font-semibold text-codialis-navy">{nom}</span>;
}

function LigneFin({ ligne }: { ligne: LigneFinFormation }): JSX.Element {
  return (
    <div className="flex flex-col items-start justify-between gap-2 px-4 py-4 transition hover:bg-gray-50/50 sm:flex-row sm:items-center sm:px-6">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <NomApprenant id={ligne.apprenantId} nom={ligne.apprenant} />
          <span className="text-codialis-gray">· {ligne.promotion}</span>
          <PastilleSite site={ligne.siteCode} />
        </div>
        <div className="mt-0.5 text-[11px] text-codialis-gray">{detailFin(ligne)}</div>
        {ligne.ecf && (
          <div className="mt-0.5 text-[11px] text-codialis-gray">
            ECF : {ligne.ecf.realisees}/{ligne.ecf.attendues} réalisée
            {ligne.ecf.realisees > 1 ? 's' : ''}, {ligne.ecf.acquises} acquise
            {ligne.ecf.acquises > 1 ? 's' : ''}
            {ligne.ecf.aSaisir > 0 ? ` · ${ligne.ecf.aSaisir} à saisir` : ''}
          </div>
        )}
      </div>
      <Pastille texte={ligne.alerteLibelle} ton={ligne.alerteTon} />
    </div>
  );
}

function CelluleResultat({ ligne }: { ligne: LigneEcf }): JSX.Element {
  if (!ligne.resultatLibelle) return <span className="text-xs text-codialis-gray">-</span>;
  return <Pastille texte={ligne.resultatLibelle} ton={ligne.resultatTon ?? 'neutral'} />;
}

function ActionsLigne({
  ligne,
  onSaisir,
  onRetirer,
  compact = false,
}: {
  ligne: LigneEcf;
  onSaisir: () => void;
  onRetirer: () => void;
  /** Dans le tableau, « Retirer » se réduit à son icône pour tenir sur une ligne. */
  compact?: boolean;
}): JSX.Element {
  const directe = useValidationDirecte();
  // Saisi, le résultat est lu par l'apprenant : sa correction est une demande à
  // la direction (TF-437), que la ligne montre tant qu'elle attend.
  const libelle =
    ligne.resultat === null
      ? ligne.refusSaisie === null
        ? 'Saisir'
        : null
      : ligne.demandeEnAttente
        ? 'Correction demandée'
        : ligne.refusCorrection === null
          ? directe
            ? 'Corriger'
            : 'Demander une correction'
          : null;
  return (
    <div className="flex flex-nowrap items-center justify-end gap-1">
      {libelle && (
        <button
          type="button"
          onClick={onSaisir}
          className={`inline-flex min-h-[36px] items-center whitespace-nowrap rounded-lg px-2.5 text-xs font-semibold hover:underline [@media(pointer:coarse)]:min-h-[40px] ${
            ligne.demandeEnAttente ? 'text-amber-700' : 'text-codialis-sky'
          }`}
        >
          {libelle}
        </button>
      )}
      {ligne.refusRetrait === null && (
        <button
          type="button"
          onClick={onRetirer}
          aria-label={`Retirer l’ECF de ${ligne.apprenant}`}
          title="Retirer cette ECF planifiée"
          className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center gap-1 rounded-lg px-2 text-xs font-semibold text-codialis-gray hover:text-red-600 [@media(pointer:coarse)]:min-h-[40px] [@media(pointer:coarse)]:min-w-[40px]"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          {!compact && 'Retirer'}
        </button>
      )}
    </div>
  );
}

/** Une évaluation à saisir : même bloc, même date, même promotion. */
interface GroupeASaisir {
  cle: string;
  libelle: string;
  lignes: LigneEcf[];
}

function groupesASaisir(lignes: LigneEcf[]): GroupeASaisir[] {
  const groupes = new Map<string, GroupeASaisir>();
  for (const ligne of lignes) {
    if (ligne.etat !== 'A_SAISIR') continue;
    const cle = `${ligne.blocId}|${ligne.datePrevue}|${ligne.promotionCle}`;
    const groupe = groupes.get(cle) ?? {
      cle,
      libelle: `${ligne.bloc} · ${jourLisible(ligne.datePrevue)} · ${ligne.promotion} (${ligne.siteCode})`,
      lignes: [],
    };
    groupe.lignes.push(ligne);
    groupes.set(cle, groupe);
  }
  return [...groupes.values()];
}

function ModaleChoixSaisie({
  ouverte,
  onFermer,
  groupes,
  onChoisir,
}: {
  ouverte: boolean;
  onFermer: () => void;
  groupes: GroupeASaisir[];
  onChoisir: (groupe: GroupeASaisir) => void;
}): JSX.Element | null {
  return (
    <Modale
      titre="Saisir les résultats ECF"
      sousTitre="Choisir l’évaluation dont la date est venue"
      ouverte={ouverte}
      onFermer={onFermer}
      largeur="max-w-xl"
    >
      <div className="p-4 sm:p-6">
        {groupes.length === 0 ? (
          <p className="text-sm text-codialis-gray">
            Aucune ECF n’attend son résultat : celles qui sont planifiées se saisissent à partir de leur
            date.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
            {groupes.map((groupe) => (
              <li key={groupe.cle}>
                <button
                  type="button"
                  onClick={() => onChoisir(groupe)}
                  className="flex min-h-[48px] w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm text-codialis-navy hover:bg-gray-50"
                >
                  <span className="min-w-0">{groupe.libelle}</span>
                  <Pastille
                    texte={`${groupe.lignes.length} apprenant${groupe.lignes.length > 1 ? 's' : ''}`}
                    ton="warning"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modale>
  );
}

function ModaleRetrait({
  ligne,
  onFermer,
}: {
  ligne: LigneEcf | null;
  onFermer: () => void;
}): JSX.Element | null {
  const notifier = useToast();
  const retirer = useRetirerEcf();
  return (
    <Modale
      titre="Retirer cette ECF ?"
      sousTitre={ligne ? `${ligne.apprenant} · ${ligne.bloc} · ${jourLisible(ligne.datePrevue)}` : ''}
      ouverte={ligne !== null}
      onFermer={() => {
        retirer.reset();
        onFermer();
      }}
      largeur="max-w-md"
    >
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-gray">
          L’ECF planifiée est retirée ; le bloc redevient à planifier pour cet apprenant.
        </p>
        {retirer.error instanceof ErreurApi && <MessageErreur message={retirer.error.message} />}
        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal
            disabled={retirer.isPending}
            onClick={() =>
              ligne &&
              retirer.mutate(ligne.id, {
                onSuccess: () => {
                  notifier('ECF retirée.');
                  onFermer();
                },
              })
            }
          >
            Retirer
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}

export function FormFinPage(): JSX.Element {
  const { site } = usePerimetre();
  const fin = useFinFormation(site);
  const ecf = useEcf(site);
  const notifications = useNotifications({});

  const [filtreFin, setFiltreFin] = useState<FiltreFin>('ALERTE');
  const [filtreEcf, setFiltreEcf] = useState<FiltreEcf>('A_SAISIR');
  const [recherche, setRecherche] = useState('');
  const [planification, setPlanification] = useState(false);
  const [choixSaisie, setChoixSaisie] = useState(false);
  const [aSaisir, setASaisir] = useState<LigneEcf[]>([]);
  const [aRetirer, setARetirer] = useState<LigneEcf | null>(null);

  const lignesFin = fin.data?.apprenants ?? [];
  const finsAffichees = filtreFin === 'ALERTE' ? lignesFin.filter(enAlerte) : lignesFin;
  const evaluations = useMemo(() => ecf.data?.evaluations ?? [], [ecf.data]);
  const groupes = useMemo(() => groupesASaisir(evaluations), [evaluations]);

  const ecfAffichees = useMemo(() => {
    const terme = recherche.trim().toLocaleLowerCase('fr');
    return evaluations.filter(
      (ligne) =>
        (filtreEcf === 'TOUTES' || ligne.etat === filtreEcf) &&
        (!terme ||
          ligne.apprenant.toLocaleLowerCase('fr').includes(terme) ||
          ligne.bloc.toLocaleLowerCase('fr').includes(terme)),
    );
  }, [evaluations, filtreEcf, recherche]);

  if (fin.isLoading || ecf.isLoading) return <Chargement libelle="Chargement des fins de formation…" />;
  const erreur = fin.error ?? ecf.error;
  if (erreur) {
    return (
      <div className="mx-auto max-w-3xl p-4 sm:p-6">
        <MessageErreur
          message={erreur instanceof ErreurApi ? erreur.message : 'Cet écran n’a pas pu être chargé.'}
        />
      </div>
    );
  }

  const compteurs = fin.data?.compteurs;
  // Paliers réglés par la direction (TF-435) ; 6 et 3 mois tant qu'ils ne sont pas lus.
  const [premierPalier, secondPalier] = fin.data?.paliers ?? [6, 3];
  const nombreParEtat = (etat: FiltreEcf): number =>
    etat === 'TOUTES' ? evaluations.length : evaluations.filter((ligne) => ligne.etat === etat).length;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Fin de formation & ECF"
        sousTitre={`Espace formateur · notifications, alertes ${secondPalier} et ${premierPalier} mois, évaluations en cours de formation`}
        actions={
          <>
            <BarreSites />
            <BoutonSecondaire onClick={() => setPlanification(true)}>
              <CalendarPlus className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Planifier une ECF
            </BoutonSecondaire>
            <BoutonPrincipal onClick={() => setChoixSaisie(true)}>
              <ClipboardCheck className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Saisir les résultats ECF
            </BoutonPrincipal>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Promotions suivies"
          valeur={compteurs?.promotions ?? 0}
          detail={`${compteurs?.suivis ?? 0} apprenant${(compteurs?.suivis ?? 0) > 1 ? 's' : ''} en formation`}
        />
        <Compteur
          libelle={`Fin dans ${premierPalier} mois`}
          valeur={compteurs?.alertePremierPalier ?? 0}
          detail={`entre ${premierPalier} et ${secondPalier} mois avant la fin`}
          ton="warm"
        />
        <Compteur
          libelle={`Fin dans ${secondPalier} mois`}
          valeur={compteurs?.alerteSecondPalier ?? 0}
          detail={`moins de ${secondPalier} mois avant la fin`}
          ton="red"
        />
        <Compteur
          libelle="ECF réalisées"
          valeur={compteurs?.ecfRealisees ?? 0}
          detail={`sur ${compteurs?.ecfAttendues ?? 0} attendue${(compteurs?.ecfAttendues ?? 0) > 1 ? 's' : ''}${
            compteurs?.ecfASaisir ? ` · ${compteurs.ecfASaisir} à saisir` : ''
          }`}
          ton="mint"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Carte
            titre="Alertes de fin de formation"
            actions={
              <Puces
                libelle="Filtrer les fins de formation"
                valeur={filtreFin}
                onChange={setFiltreFin}
                options={[
                  { valeur: 'ALERTE', libelle: 'À traiter', nombre: lignesFin.filter(enAlerte).length },
                  { valeur: 'TOUS', libelle: 'Tous', nombre: lignesFin.length },
                ]}
              />
            }
          >
            <p className="px-4 py-2 text-xs text-codialis-gray sm:px-6">
              Surveillance des jalons réglementaires d’examen : alerte six mois, puis trois mois avant
              la fin.
            </p>
            {finsAffichees.length === 0 ? (
              <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">
                {filtreFin === 'ALERTE'
                  ? 'Aucune fin de formation dans les six mois, ni date de fin manquante.'
                  : 'Aucun apprenant en formation dans ce périmètre.'}
              </p>
            ) : (
              finsAffichees.map((ligne) => <LigneFin key={ligne.apprenantId} ligne={ligne} />)
            )}
          </Carte>

          <Carte
            titre="Évaluations en cours de formation (ECF)"
            actions={
              <Puces
                libelle="Filtrer les ECF"
                valeur={filtreEcf}
                onChange={setFiltreEcf}
                options={FILTRES_ECF.map((option) => ({ ...option, nombre: nombreParEtat(option.valeur) }))}
              />
            }
          >
            <div className="px-4 py-3 sm:px-6">
              <p className="mb-2 text-xs text-codialis-gray">
                Grilles d’évaluation continue par bloc de compétences. Le résultat saisi fait foi et se
                lit par l’apprenant ; sa correction se demande à la direction, motif à l’appui, et
                l’ancien résultat reste tracé.
              </p>
              <label className="relative block max-w-sm">
                <span className="sr-only">Rechercher un apprenant ou un bloc</span>
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
                  aria-hidden="true"
                />
                <input
                  value={recherche}
                  onChange={(evenement) => setRecherche(evenement.target.value)}
                  placeholder="Apprenant ou bloc"
                  className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm text-codialis-navy"
                />
              </label>
            </div>

            {ecfAffichees.length === 0 ? (
              <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">
                {evaluations.length === 0
                  ? 'Aucune ECF planifiée. « Planifier une ECF » choisit un bloc, une date et les apprenants d’une promotion.'
                  : 'Aucune ECF ne correspond à ce filtre.'}
              </p>
            ) : (
              <>
                {/* Tablette et bureau : le tableau de la maquette. */}
                <div className="hidden md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold">Apprenant</th>
                        <th className="px-4 py-3 text-left font-bold">Bloc évalué</th>
                        <th className="px-4 py-3 text-left font-bold">Date</th>
                        <th className="px-4 py-3 text-left font-bold">Résultat</th>
                        <th className="px-4 py-3 text-left font-bold">Statut</th>
                        <th className="px-4 py-3 text-right font-bold">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ecfAffichees.map((ligne) => (
                        <tr key={ligne.id} className="align-top transition hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <NomApprenant id={ligne.apprenantId} nom={ligne.apprenant} />
                            <div className="text-[11px] text-codialis-gray">{ligne.promotion}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-codialis-gray">
                            {/* Les intitulés de bloc reprennent le référentiel, souvent
                                longs : deux lignes, l'intitulé entier au survol. */}
                            <span className="line-clamp-2" title={ligne.bloc}>
                              {ligne.bloc}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-codialis-gray">
                            {jourLisible(ligne.datePrevue)}
                          </td>
                          <td className="px-4 py-3">
                            <CelluleResultat ligne={ligne} />
                          </td>
                          <td className="px-4 py-3">
                            <Pastille texte={ligne.etatLibelle} ton={ligne.etatTon} />
                          </td>
                          <td className="px-2 py-2 text-right">
                            <ActionsLigne
                              compact
                              ligne={ligne}
                              onSaisir={() => setASaisir([ligne])}
                              onRetirer={() => setARetirer(ligne)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile : une carte par ECF. */}
                <ul className="divide-y divide-gray-100 md:hidden">
                  {ecfAffichees.map((ligne) => (
                    <li key={ligne.id} className="space-y-1.5 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5 text-sm">
                        <NomApprenant id={ligne.apprenantId} nom={ligne.apprenant} />
                        <Pastille texte={ligne.etatLibelle} ton={ligne.etatTon} />
                      </div>
                      <div className="text-xs text-codialis-gray">
                        {ligne.bloc} · {jourLisible(ligne.datePrevue)}
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <CelluleResultat ligne={ligne} />
                        <ActionsLigne
                          ligne={ligne}
                          onSaisir={() => setASaisir([ligne])}
                          onRetirer={() => setARetirer(ligne)}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Carte>
        </div>

        <div className="min-w-0 space-y-6">
          <Carte
            titre="Mes notifications"
            actions={
              <Link
                to="/notifications"
                className="inline-flex min-h-[32px] items-center text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
              >
                Tout voir
              </Link>
            }
          >
            {(notifications.data?.notifications ?? []).length === 0 ? (
              <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">
                {notifications.isLoading ? 'Chargement…' : 'Aucune notification.'}
              </p>
            ) : (
              (notifications.data?.notifications ?? []).slice(0, 6).map((notification) => (
                <div key={notification.id} className="flex items-start gap-3 px-4 py-3.5 sm:px-6">
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      notification.lu ? 'bg-gray-300' : 'bg-codialis-sky'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-codialis-navy">{notification.titre}</div>
                    <div className="text-[11px] text-codialis-gray">{notification.corps}</div>
                    <div className="mt-0.5 text-[10px] text-codialis-gray">
                      {new Date(notification.envoyeeLe).toLocaleDateString('fr-FR', {
                        timeZone: 'Indian/Reunion',
                      })}
                    </div>
                  </div>
                </div>
              ))
            )}
          </Carte>
        </div>
      </div>

      <ModalePlanificationEcf
        ouverte={planification}
        onFermer={() => setPlanification(false)}
        promotions={ecf.data?.promotions ?? []}
      />
      <ModaleChoixSaisie
        ouverte={choixSaisie}
        onFermer={() => setChoixSaisie(false)}
        groupes={groupes}
        onChoisir={(groupe) => {
          setChoixSaisie(false);
          setASaisir(groupe.lignes);
        }}
      />
      <ModaleSaisieEcf
        ouverte={aSaisir.length > 0}
        onFermer={() => setASaisir([])}
        lignes={aSaisir}
        titre={aSaisir.length === 1 && aSaisir[0]?.resultat ? 'Corriger le résultat ECF' : 'Saisir les résultats ECF'}
      />
      <ModaleRetrait ligne={aRetirer} onFermer={() => setARetirer(null)} />
    </div>
  );
}
